/**
 * @class ProductCardSlider
 * @classdesc A class for handling product card slider interactions
 * @extends Core.Component
 */
export default class ProductCardSlider extends Core.Component {
  constructor(el, context) {
    super(el, context);
  }

  onMount() {
    this.slider = this.el.querySelector('[data-slider]');
    this.sliderItems = this.slider.querySelectorAll('[data-slider-item]');
    this.buttonContainer = this.el.querySelector('[data-product-card-slider-buttons]');
    this.previousButton = this.buttonContainer.querySelector('[data-slider-button-previous]');
    this.nextButton = this.buttonContainer.querySelector('[data-slider-button-next]');
    this.scrollIncrementBreakpoint = this.data.scrollIncrementBp;

    this._setScrollIncrement();
    this._setButtonContainerHeight();

    this.events.register(this.previousButton, 'click', event => this._handleClick(event));
    this.events.register(this.nextButton, 'click', event => this._handleClick(event));
    this.events.register(window, 'resize', () => this._setButtonContainerHeight());
    Core.Breakpoints.onChange(() => this._setScrollIncrement());

    // The 10% rootMargin is to account for the 10% "sneak peak" of the next card in the slider
    this._observer = new IntersectionObserver(this._handleIntersection.bind(this), { root: this.slider, rootMargin: '0px 10% 0px 0px', threshold: 1.0 });
    this.sliderItems.forEach(item => this._observer.observe(item));

  }

  /**
   * Handles the click event for when the previous/next buttons are clicked.
   * Slider scrolls one card-length per click.
   * @param {Event} event - The 'click' event object
   */
  _handleClick(event) {
    event.preventDefault();

    const { name } = event.currentTarget;
    const newScrollPosition = name === 'next'
      ? this.slider.scrollLeft + this.scrollIncrement
      : this.slider.scrollLeft - this.scrollIncrement;

    this.slider.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
  }

  /**
   *
   * Callback for IntersectionObserver.
   * Toggle button visibility based on users scroll position in slider
   * @param {IntersectionObserverEntry[]} entries
   */
  _handleIntersection(entries) {
    // Safari doesn't support `scrollTo`'s `smooth` behavior. Therefore,
    // process the entries in batches as multiple cards may become visible
    // in a single scroll movement
    const intersectionCardIndices = entries
      .filter(entry => entry.isIntersecting)
      .map(({ target }) => Array.prototype.indexOf.call(this.sliderItems, target));

    if (intersectionCardIndices.length === 0) return;

    const containsFirstCard = intersectionCardIndices.includes(0);
    const containsLastCard = intersectionCardIndices.includes(this.sliderItems.length - 1);

    this.previousButton.classList.toggle('hidden', containsFirstCard);
    this.nextButton.classList.toggle('hidden', containsLastCard);
  }

  /**
   * Determine how many cards to scroll when previous/next buttons
   * are clicked.
   */
  _setScrollIncrement() {
    this.multiplier = Core.Breakpoints.min(this.scrollIncrementBreakpoint)
      ? Number(this.data.cardsPerRow, 10)
      : Number(this.data.cardsPerRowMobile, 10);

    if (this.sliderItems[0] !== undefined) {
      this.scrollIncrement = this.sliderItems[0].clientWidth * this.multiplier;
    }
  }

  /**
   * Manually set button container height so buttons are centred
   * relative to product card images
   */
  _setButtonContainerHeight() {
    if (!this.buttonContainer) return;
    const { offsetHeight } = this.slider.querySelector('[data-product-image]');
    this.buttonContainer.style.height = `${offsetHeight}px`;
  }
}
